import { Controller } from "react-hook-form";
import { Calendar } from "@demark-pro/react-booking-calendar";
import { useState, useEffect } from "react";

interface CalendarBlockProps {
  control: any;
  setValue: Function;
  getValues: Function;
  availableWindows: any[];
  filteredBlockedDates: { startDate: Date; endDate: Date }[];
  calendarMonth: number;
  calendarYear: number;
  setCalendarMonth: (month: number) => void;
  setCalendarYear: (year: number) => void;
  normalizeDate: (d: Date) => Date;
  dayKey: (d: Date) => string;
  checkDateOverlap: (range: Date[], blocked: { startDate: Date; endDate: Date }[]) => boolean;
  dts: Date[];
  setSelectedWindow: (w: any) => void;
  add_days: (date: Date, days: number) => Date;
  isRoomMode?: boolean;
  setNumNights?: (nights: number) => void;
  enrichedWindows: any[];
  onCalendarMount?: () => void;   // 👈 add this

}

// Helper: check if a selection overlaps any full window
function checkFullOverlap(resRange: Date[], enrichedWindows: any[], normalizeDate: (d: Date) => Date) {
  if (!Array.isArray(enrichedWindows) || enrichedWindows.length === 0) {
    // console.warn("⚠️ checkFullOverlap: enrichedWindows is missing or empty");
    return false;
  }

  return enrichedWindows.some((w) => {
    const status = w.status?.toLowerCase?.();
    if (status !== "full") return false;

    const wStart = normalizeDate(w.start);
    const wEnd = normalizeDate(w.end);
    const hit = resRange[0] <= wEnd && resRange[1] >= wStart;

    if (hit) {
      // console.log("⛔ Full window overlap detected", {
      //   className: w.className,
      //   status: w.status,
      //   start: wStart.toDateString(),
      //   end: wEnd.toDateString(),
      //   resRange: resRange.map((d) => d.toDateString()),
      // });
    }

    return hit;
  });
}
export default function CalendarBlock({
  control,
  setValue,
  getValues,
  availableWindows,
  filteredBlockedDates,
  calendarMonth,
  calendarYear,
  setCalendarMonth,
  setCalendarYear,
  normalizeDate,
  dayKey,
  checkDateOverlap,
  dts,
  setSelectedWindow,
  add_days,
  isRoomMode = false,
  setNumNights,
  enrichedWindows,
  onCalendarMount,   // 👈 receive this prop
}: CalendarBlockProps) {
  console.log("📆 CalendarBlock props:", {
  month: calendarMonth,
  year: calendarYear,
  reserved: filteredBlockedDates,
  availableWindows,
  enrichedWindows,
  isRoomMode,
});
useEffect(() => {
  onCalendarMount?.();   // 👈 call it once, after first render
}, []);
  return (
    <div className="calendar-root">
      <Controller
        name="dates"
        control={control}
        rules={{ required: true, validate: true }}
        render={({ field }) => {
          // Preflight logging
          // console.log("📅 Calendar render preflight", {
          //   isRoomMode,
          //   fieldValue: field.value,
          //   selectedShape: Array.isArray(field.value) ? "array" : typeof field.value,
          //   selectedLen: Array.isArray(field.value) ? field.value.length : 0,
          // });

          const selectedArray = Array.isArray(field.value) ? field.value : [];

          // Calendar gets full range in room mode, auto-generated range in class mode
          const finalSelected = isRoomMode
            ? selectedArray
            : selectedArray.length === 2
            ? selectedArray
            : selectedArray.length === 1
            ? [selectedArray[0], normalizeDate(add_days(selectedArray[0], getValues("Experience") === "1" ? 6 : 4))]
            : [];

          // console.log("📅 Calendar selected prop", {
          //   isRoomMode,
          //   finalSelected,
          //   type: Array.isArray(finalSelected) ? "array" : typeof finalSelected,
          //   len: Array.isArray(finalSelected) ? finalSelected.length : 0,
          // });

          return (
            
              <Calendar
                selected={finalSelected}
                reserved={filteredBlockedDates}
                year={calendarYear ?? new Date().getFullYear()}
                month={calendarMonth ?? new Date().getMonth()}
                range={true}
                protection={true}
                options={{
                  locale: "en",
                  weekStartsOn: 0,
                  useAttributes: true,
                }}
                onMonthChange={(newMonth) => setCalendarMonth(newMonth)}
                onYearChange={(newYear) => setCalendarYear(newYear)}
                onOverbook={(date, type) => {
                  console.warn("⚠️ Overbook event", { date, type });
                  alert(type);
                }}
                onChange={(raw) => {
                  // console.log("📆 onChange fired", {
                  //   rawType: Array.isArray(raw) ? "array" : typeof raw,
                  //   raw,
                  //   isRoomMode,
                  // });
                  if (!raw) return;

                  const clicks = Array.isArray(raw) ? raw : [raw];

                  // -----------------------
                  // Room mode (range=true)
                  // -----------------------
                  if (isRoomMode) {
                    if (clicks.length === 1) {
                      const startDate = normalizeDate(new Date(clicks[0]));
                      // console.log("✅ RoomMode first click", { startDate });
                      field.onChange([startDate]);
                      return;
                    }

                    if (clicks.length === 2) {
                      const startDate = normalizeDate(new Date(clicks[0]));
                      const endDate = normalizeDate(new Date(clicks[1]));
                      const nights = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
                      setNumNights?.(nights);

                      // console.log("✅ RoomMode second click", {
                      //   startDate,
                      //   endDate,
                      //   nights,
                      // });

                      if (nights < 2) {
                        // console.warn("⛔ Nights too short", { nights });
                        alert("Minimum stay is 2 nights.");
                        field.onChange([]);
                        setValue("dates", [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        return;
                      }

                      const resRange = [startDate, endDate];
                      // console.log("🔍 Selection attempt", {
                      //   resRange: resRange.map((d) => d.toDateString()),
                      //   filteredBlockedDates: filteredBlockedDates.map((r) => ({
                      //     start: r.startDate.toDateString(),
                      //     end: r.endDate.toDateString(),
                      //   })),
                      //   availableWindows: availableWindows.map((w) => ({
                      //     className: w.className,
                      //     status: w.status,
                      //     start: w.start.toDateString(),
                      //     end: w.end.toDateString(),
                      //   })),
                      // });
                      const overlapsBlocked = checkDateOverlap(resRange, filteredBlockedDates);
                      const overlapsScheduled = availableWindows.some((w) => {
                        const wStart = normalizeDate(w.start);
                        const wEnd = normalizeDate(w.end);
                        return resRange[0] <= wEnd && resRange[1] >= wStart;
                      });
                      const overlapsFull = checkFullOverlap(resRange, enrichedWindows, normalizeDate);
                      // console.log("✅ Overlap results", {
                      //   overlapsBlocked,
                      //   overlapsScheduled,
                      //   overlapsFull,
                      // });

                      if (overlapsBlocked || overlapsScheduled || overlapsFull) {
                        // console.warn("⛔ RoomMode overlap", {
                        //   overlapsBlocked,
                        //   overlapsScheduled,
                        //   overlapsFull,
                        // });
                        alert("The selected dates are unavailable.");
                        field.onChange([]);
                        setValue("dates", [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        return;
                      }

                      setSelectedWindow(null);
                      field.onChange(resRange);
                      setValue("dates", resRange, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      return;
                    }

                    return;
                  }

                  // -----------------------
                  // Class mode (range=false)
                  // -----------------------
                  if (clicks.length === 0) return;
                  const selectedDate = normalizeDate(new Date(clicks[0]));
                  // console.log("✅ ClassMode click", { selectedDate });

                  const match = availableWindows.find(
                    ({ start, end }) => dayKey(selectedDate) >= dayKey(start) && dayKey(selectedDate) <= dayKey(end)
                  );

                  if (match) {
                    // console.log("✅ ClassMode matched fixed window", match);
                    setSelectedWindow(match);
                    const scheduledRange = [normalizeDate(match.start), normalizeDate(match.end)];
                    field.onChange(scheduledRange); // calendar sees full range
                    setValue("dates", scheduledRange, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue("ExperienceTitle", match.className, {
                      shouldValidate: true,
                    });
                    setValue("Experience", match.duration, {
                      shouldValidate: true,
                    });
                    return;
                  }

                  const defaultDuration = getValues("Experience") === "1" ? 6 : 4;
                  const endDate = normalizeDate(add_days(selectedDate, defaultDuration));
                  const resRange = [selectedDate, endDate];
                  // console.log("🔍 Selection attempt", {
                  //   resRange: resRange.map((d) => d.toDateString()),
                  //   filteredBlockedDates: filteredBlockedDates.map((r) => ({
                  //     start: r.startDate.toDateString(),
                  //     end: r.endDate.toDateString(),
                  //   })),
                  //   availableWindows: availableWindows.map((w) => ({
                  //     className: w.className,
                  //     status: w.status,
                  //     start: w.start.toDateString(),
                  //     end: w.end.toDateString(),
                  //   })),
                  // });
                  // console.log("✅ ClassMode free selection", {
                  //   resRange,
                  //   defaultDuration,
                  // });

                  const overlapsBlocked = checkDateOverlap(resRange, filteredBlockedDates);
                  const overlapsScheduled = availableWindows.some((w) => {
                    const wStart = normalizeDate(w.start);
                    const wEnd = normalizeDate(w.end);
                    return resRange[0] <= wEnd && resRange[1] >= wStart;
                  });
                  const overlapsFull = checkFullOverlap(resRange, enrichedWindows, normalizeDate);
                  // console.log("✅ Overlap results", {
                  //   overlapsBlocked,
                  //   overlapsScheduled,
                  //   overlapsFull,
                  // });
                  if (overlapsBlocked || overlapsScheduled || overlapsFull) {
                    // console.warn("⛔ ClassMode overlap", {
                    //   overlapsBlocked,
                    //   overlapsScheduled,
                    //   overlapsFull,
                    // });
                    alert("The selected dates are unavailable.");
                    field.onChange([]);
                    setValue("dates", [], {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    return;
                  }

                  setSelectedWindow(null);
                  field.onChange(resRange); // calendar sees full range
                  setValue("dates", resRange, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            
          );
        }}
      />
    </div>
  );
}
